import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { Country } from '../data/countries';

interface CountryComboboxProps {
  id: string;
  label: string;
  placeholder: string;
  noResultsText: string;
  countries: Country[];
  selectedCountry: string;
  onCountryChange: (countryCode: string) => void;
}

function CountryCombobox({
  id,
  label,
  placeholder,
  noResultsText,
  countries,
  selectedCountry,
  onCountryChange,
}: CountryComboboxProps): React.ReactElement {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const rootRef = useRef<HTMLDivElement>(null);

  const selectedCountryLabel = useMemo(() => {
    const match = countries.find((country) => country.code === selectedCountry);
    return match ? match.name : '';
  }, [countries, selectedCountry]);

  const filteredCountries = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery) {
      return countries;
    }

    return countries.filter((country) => {
      const nameMatch = country.name.toLowerCase().includes(normalizedQuery);
      const codeMatch = country.code.toLowerCase().includes(normalizedQuery);
      return nameMatch || codeMatch;
    });
  }, [countries, query]);

  useEffect(() => {
    setQuery(selectedCountryLabel);
  }, [selectedCountryLabel]);

  useEffect(() => {
    const handlePointerOutside = (event: MouseEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    };

    document.addEventListener('mousedown', handlePointerOutside);

    return () => {
      document.removeEventListener('mousedown', handlePointerOutside);
    };
  }, []);

  const listboxId = `${id}-listbox`;

  const handleOptionSelect = (country: Country): void => {
    onCountryChange(country.code);
    setQuery(country.name);
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setQuery(event.target.value);
    setIsOpen(true);
    setActiveIndex(0);
  };

  const handleInputFocus = (): void => {
    setIsOpen(true);
    setActiveIndex(filteredCountries.length > 0 ? 0 : -1);
  };

  const handleInputBlur = (): void => {
    const exactMatch = countries.find((country) => country.name.toLowerCase() === query.trim().toLowerCase());

    if (exactMatch) {
      onCountryChange(exactMatch.code);
      setQuery(exactMatch.name);
      return;
    }

    if (!query.trim()) {
      onCountryChange('');
      return;
    }

    if (selectedCountryLabel) {
      setQuery(selectedCountryLabel);
      return;
    }

    setQuery('');
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsOpen(true);

      if (filteredCountries.length > 0) {
        setActiveIndex((current) => {
          if (current < 0) {
            return 0;
          }

          return (current + 1) % filteredCountries.length;
        });
      }
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();

      if (filteredCountries.length > 0) {
        setActiveIndex((current) => {
          if (current < 0) {
            return filteredCountries.length - 1;
          }

          return (current - 1 + filteredCountries.length) % filteredCountries.length;
        });
      }
      return;
    }

    if (event.key === 'Enter') {
      if (isOpen && activeIndex >= 0 && filteredCountries[activeIndex]) {
        event.preventDefault();
        handleOptionSelect(filteredCountries[activeIndex]);
      }
      return;
    }

    if (event.key === 'Escape') {
      setIsOpen(false);
      setActiveIndex(-1);
      setQuery(selectedCountryLabel);
      return;
    }

    if (event.key === 'Tab') {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div className="country-combobox" ref={rootRef}>
      <label className="welcome-block__language-label" htmlFor={id}>
        {label}:
      </label>
      <div className="country-combobox__control">
        <input
          id={id}
          type="text"
          className="welcome-block__language-select country-combobox__input"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            isOpen && activeIndex >= 0 && filteredCountries[activeIndex]
              ? `${id}-option-${filteredCountries[activeIndex].code}`
              : undefined
          }
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
        />

        {isOpen && (
          <ul className="country-combobox__list" role="listbox" id={listboxId}>
            {filteredCountries.length === 0 ? (
              <li className="country-combobox__empty">{noResultsText}</li>
            ) : (
              filteredCountries.map((country, index) => {
                const isActive = index === activeIndex;
                const isSelected = country.code === selectedCountry;

                return (
                  <li
                    key={country.code}
                    id={`${id}-option-${country.code}`}
                    role="option"
                    aria-selected={isSelected}
                    className={`country-combobox__option${isActive ? ' country-combobox__option--active' : ''}`}
                    onMouseDown={(event) => {
                      event.preventDefault();
                    }}
                    onClick={() => handleOptionSelect(country)}
                  >
                    <span className="country-combobox__name">{country.name}</span>
                    <span className="country-combobox__code">{country.code}</span>
                  </li>
                );
              })
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

export default CountryCombobox;
